import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  CampaignAudience,
  StoreResponse,
  TermsBucket,
  TermsResult,
  TermsSpreadEntry,
} from '@clarity/contract';
import { useCampaignTerms } from '../../queries/index.js';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { isOnboardingRequired, money, num } from '../format.js';

/**
 * The Terms view (clarity-campaign-terms-economics task 6.2, Req 6.1, 6.3, 6.4),
 * rebuilt from the agreed mock `brand/campaigns-terms-mock.html` (VIEW · TERMS
 * LIST) in the teal-glass world. The mock is the contract.
 *
 * Terms is a PARALLEL reporting axis over the SAME discounted orders the matcher
 * attributed — grouped by the discount's economic SIGNATURE (percentage, min,
 * cap, audience, item-scope), not by campaign. Each `.term` card carries the
 * gating chips, the order count + restaurant-funded discount (`--font-num`), and
 * a `.spread` bar showing how the bucket's orders split across campaigns + the
 * striped `unexplained` slice (orders no campaign explains). The card expands to
 * the per-campaign spread breakdown (the terms endpoint returns buckets + spread,
 * NOT per-order rows, so the expansion renders the spread + the reporting note —
 * it does not invent an order list the API does not return).
 *
 * Driven by `useCampaignTerms(store.id, …)` over the typed ts-rest client; the
 * loading / empty / error states mirror {@link StoreCampaignsList}. Real tokens
 * only (no hexes), `--font-num` on every figure, logical properties for RTL, and
 * WCAG 2.1 AA (the spread bar names each slice in text, not colour alone).
 */
export function TermsView({ store }: { store: StoreResponse }) {
  const query = useCampaignTerms(store.id, undefined, undefined, !isNotYetOnboarded(store));
  const onboardingBlocked = isOnboardingRequired(query.error);

  if (query.isLoading) return <TermsLoading />;
  // A `409` is the expected not-yet-onboarded block — surface it as the empty
  // state, not an error (same contract as the campaigns table).
  if (query.isError && !onboardingBlocked) return <TermsError />;

  const data: TermsResult | null = query.data?.status === 200 ? query.data.body : null;

  if (!data || data.buckets.length === 0) {
    return <TermsEmpty />;
  }

  return <TermsLoaded store={store} data={data} />;
}

// ── The loaded Terms axis ──────────────────────────────────────────────────────

const currency = (store: StoreResponse) => store.profile?.currency ?? 'JOD';

function TermsLoaded({ store, data }: { store: StoreResponse; data: TermsResult }) {
  const cur = currency(store);
  const { summary } = data;
  const tiedPct = Math.round(summary.tiedPct * 100);

  return (
    <div className="terms-view">
      <div className="ev-note" role="note">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5M12 8h.01" />
        </svg>
        <p>
          Terms is a <b>reporting view</b>, not a matcher. Campaign attribution is unchanged — this
          groups the same orders by what the customer actually got, so you can see a discount band
          (like <b>20% off</b>) even when no single campaign explains all of it. The striped slice is
          orders we could not tie to any campaign.
        </p>
      </div>

      <div className="campaigns__summary">
        <div className="card stat">
          <h4 className="stat__label">Discounted orders</h4>
          <div className="stat__value">{num(summary.discountedOrders)}</div>
          <div className="stat__sub">
            across {num(data.buckets.length)} distinct term{data.buckets.length === 1 ? '' : 's'}
          </div>
        </div>
        <div className="card stat">
          <h4 className="stat__label">Total discount</h4>
          <div className="stat__value">
            {num(round0(summary.discountTotal))}
            <span className="unit">{cur}</span>
          </div>
          <div className="stat__sub">restaurant-funded portion</div>
        </div>
        <div className="card stat">
          <h4 className="stat__label">Tied to a campaign</h4>
          <div className="stat__value">
            {num(tiedPct)}
            <span className="unit">%</span>
          </div>
          <div className="stat__sub">
            {num(summary.discountedOrders - summary.unexplained)} of {num(summary.discountedOrders)}{' '}
            orders
          </div>
        </div>
        <div className="card stat">
          <h4 className="stat__label">Unexplained</h4>
          <div className="stat__value terms-view__danger">{num(summary.unexplained)}</div>
          <div className="stat__sub">no campaign / occurrence / terms fits</div>
        </div>
      </div>

      <div className="terms">
        {data.buckets.map((bucket) => (
          <TermCard key={bucket.key} store={store} bucket={bucket} currency={cur} />
        ))}
      </div>
    </div>
  );
}

/** The stable sentinel key the Terms axis gives the single Unexplained bucket
 *  (`aggregateTerms`): every unmatched discounted order, grouped on one bucket
 *  with no declared term. It is titled "Unexplained", never "0% off". */
const UNEXPLAINED_KEY = 'unexplained';

/**
 * True for the single Unexplained bucket — orders no campaign explains. Detected
 * by the sentinel key (the contract guarantees it; a defensive fallback also
 * treats a bucket whose entire spread is the unexplained slice as unexplained).
 */
function isUnexplained(bucket: TermsBucket): boolean {
  if (bucket.key === UNEXPLAINED_KEY) return true;
  return bucket.spread.length > 0 && bucket.spread.every((e) => e.unexplained);
}

const AUDIENCE_LABEL: Record<CampaignAudience, string> = {
  all: 'all customers',
  plus: 'Plus (TPRO) only',
  nonplus: 'non-Plus customers',
  new_customer: 'new customers',
};

/** The teal palette the spread segments cycle through (the striped `unexplained`
 *  slice draws its own pattern in CSS, keyed off `.spread__seg--unm`). */
const SPREAD_TEALS = [
  'var(--teal-400)',
  'var(--teal-600)',
  'var(--cyan-400)',
  'var(--teal-200)',
  'var(--violet-400, #a78bfa)',
];

/**
 * One term bucket card: the economic-signature header (percentage + gating
 * chips), the order count + restaurant-funded discount, the spread bar + legend,
 * expandable to the per-campaign spread breakdown + the reporting note. For an
 * item-scoped bucket, the expanded detail surfaces a "see deal economics"
 * affordance pointing at the bucket's primary campaign detail route (the
 * economics panel itself is task 6.3).
 */
function TermCard({
  store,
  bucket,
  currency,
}: {
  store: StoreResponse;
  bucket: TermsBucket;
  currency: string;
}) {
  const [open, setOpen] = useState(false);
  const bodyId = `term-body-${bucket.key.replace(/[^a-zA-Z0-9]+/g, '-')}`;
  const unexplained = isUnexplained(bucket);

  return (
    <div className="card term" aria-expanded={open}>
      <button
        type="button"
        className="term__head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
      >
        <div className="term__sig">
          <div className={`term__pct num${unexplained ? ' term__pct--unm' : ''}`}>
            {unexplained ? (
              'Unexplained'
            ) : (
              <>
                {num(bucket.percentage)}% off
                {bucket.itemScope.scoped ? ' · selected items' : ''}
              </>
            )}
          </div>
          <div className="term__chips">
            {unexplained ? (
              <span className="gchip gchip--unm">no campaign / terms fit</span>
            ) : (
              <GatingChips bucket={bucket} currency={currency} />
            )}
          </div>
        </div>
        <div className="term__fig">
          <div className="n num">
            {num(bucket.orderCount)} <span className="term__fig-unit">orders</span>
          </div>
          <div className="s num">{money(bucket.discountTotal, currency)} discount</div>
        </div>
        <span className="term__chev" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      <SpreadBar bucket={bucket} />

      <div className="term__body" id={bodyId} hidden={!open}>
        <div className="term__detail">
          <p className="term__note muted">
            {unexplained
              ? 'These discounted orders reconcile against no campaign, occurrence, or terms we model — they are counted honestly here rather than forced onto a deal.'
              : bucket.itemScope.scoped
                ? 'Item-scoped discount on the named products — these orders reconcile against the scoped sub-basket. The breakdown below is how they split across campaigns.'
                : 'How this term band’s orders split across the campaigns they matched, plus the orders no single campaign explains.'}
          </p>
          <SpreadDetail bucket={bucket} />
          {!unexplained && bucket.itemScope.scoped && (
            <ItemScopedAffordance store={store} bucket={bucket} />
          )}
        </div>
      </div>
    </div>
  );
}

/** The economic-signature chips: percentage, item-scope, min, cap, audience —
 *  in the mock's order. Each carries text + an optional icon (never colour alone). */
function GatingChips({ bucket, currency }: { bucket: TermsBucket; currency: string }) {
  return (
    <>
      <span className="gchip">{num(bucket.percentage)}% off</span>
      {bucket.itemScope.scoped && (
        <span className="gchip gchip--scope">
          <ScopeIcon />
          selected · {bucket.itemScope.label ?? 'items'}
        </span>
      )}
      {bucket.minOrder !== null && (
        <span className="gchip">min {num(bucket.minOrder)} {currency}</span>
      )}
      {bucket.cap !== null && (
        <span className="gchip gchip--cap">
          <CapIcon />
          cap {money(bucket.cap, currency)}
        </span>
      )}
      <span className="gchip gchip--aud">
        <AudIcon />
        {AUDIENCE_LABEL[bucket.audience]}
      </span>
    </>
  );
}

/**
 * The spread bar: one teal segment per matched-campaign slice (width ∝ orders),
 * and a striped slice for the `unexplained` entry. The segments sum to the
 * bucket's order count (the contract guarantees it). Each slice is also named in
 * the legend below with its count, so the information is carried by text, not
 * colour alone (WCAG 1.4.1).
 */
function SpreadBar({ bucket }: { bucket: TermsBucket }) {
  const total = bucket.orderCount || 1;
  return (
    <div className="spread">
      <div
        className="spread__bar"
        role="img"
        aria-label={spreadAria(bucket)}
      >
        {bucket.spread.map((entry, i) => {
          const width = (entry.orders / total) * 100;
          const style = entry.unexplained
            ? { inlineSize: `${width}%` }
            : { inlineSize: `${width}%`, background: segColor(i) };
          return (
            <div
              key={`${entry.label}-${i}`}
              className={`spread__seg${entry.unexplained ? ' spread__seg--unm' : ''}`}
              style={style}
              title={`${spreadLabel(entry)}: ${entry.orders}`}
            />
          );
        })}
      </div>
      <div className="spread__legend">
        {bucket.spread.map((entry, i) => (
          <span key={`${entry.label}-legend-${i}`}>
            <span
              className={`spread__sw${entry.unexplained ? ' spread__sw--unm' : ''}`}
              style={entry.unexplained ? undefined : { background: segColor(i) }}
              aria-hidden="true"
            />
            {spreadLabel(entry)} · <span className="num">{num(entry.orders)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** The expanded per-campaign spread breakdown — a compact table of the slices
 *  the bar shows, with the unexplained slice marked. No per-order rows: the
 *  terms endpoint returns buckets + spread, not order evidence. */
function SpreadDetail({ bucket }: { bucket: TermsBucket }) {
  if (bucket.spread.length === 0) {
    return (
      <div className="empty" role="status">
        <p className="empty__sub">
          {num(bucket.orderCount)} order{bucket.orderCount === 1 ? '' : 's'} carry these terms.
        </p>
      </div>
    );
  }
  return (
    <table className="term__spread-table" aria-label="Spread across campaigns">
      <thead>
        <tr>
          <th scope="col">Attributed to</th>
          <th scope="col" className="num-col">
            Orders
          </th>
        </tr>
      </thead>
      <tbody>
        {bucket.spread.map((entry, i) => (
          <tr key={`${entry.label}-row-${i}`}>
            <td>
              {entry.unexplained ? (
                <span className="pill pill--unm">unexplained</span>
              ) : (
                <span className="pill pill--matched">{entry.label}</span>
              )}
            </td>
            <td className="num-col num">{num(entry.orders)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The item-scoped affordance (Req 6.2 lead-in): a link to the bucket's primary
 * campaign detail, where the deal-economics (food-cost margin) panel lives (task
 * 6.3). We link to the first NON-unexplained spread slice carrying a campaignId;
 * if the bucket has no attributable campaign (only an unexplained slice), there
 * is no detail target to link to, so the affordance is omitted rather than
 * pointing nowhere.
 */
function ItemScopedAffordance({ store, bucket }: { store: StoreResponse; bucket: TermsBucket }) {
  const { clientId } = useParams();
  const primary = useMemo(
    () => bucket.spread.find((e) => !e.unexplained && e.campaignId !== null),
    [bucket.spread],
  );
  if (!clientId || !primary || primary.campaignId === null) return null;
  return (
    <a
      className="term__economics-link"
      href={`/c/${clientId}/store/${store.id}/campaigns/${primary.campaignId}`}
    >
      See deal economics (food cost) →
    </a>
  );
}

// ── Empty / loading / error states (mirroring StoreCampaignsList) ──────────────

function TermsEmpty() {
  return (
    <div className="terms-view">
      <div className="card empty" role="status">
        <div className="empty__title">No discounted orders in this period</div>
        <p className="empty__sub">
          Terms groups discounted orders by the discount they carried. There are no discounted orders
          in range yet — they appear here on the next data sync.
        </p>
      </div>
    </div>
  );
}

function TermsLoading() {
  return (
    <div className="terms-view">
      <div className="card campaigns__loading" role="status" aria-busy="true" aria-live="polite">
        <span className="sr-only">Grouping discounted orders by terms…</span>
        <span className="sk" style={{ inlineSize: '60%' }} aria-hidden="true" />
        <span className="sk" style={{ inlineSize: '90%' }} aria-hidden="true" />
        <span className="sk" style={{ inlineSize: '75%' }} aria-hidden="true" />
        <span className="sk" style={{ inlineSize: '85%' }} aria-hidden="true" />
      </div>
    </div>
  );
}

function TermsError() {
  return (
    <div className="terms-view">
      <p className="error" role="alert">
        Could not load terms for this branch.
      </p>
    </div>
  );
}

// ── Small pure helpers + icons ─────────────────────────────────────────────────

function round0(n: number): number {
  return Math.round(n);
}

function segColor(i: number): string {
  return SPREAD_TEALS[i % SPREAD_TEALS.length] as string;
}

function spreadLabel(entry: TermsSpreadEntry): string {
  return entry.unexplained ? 'unexplained' : entry.label;
}

function spreadAria(bucket: TermsBucket): string {
  const parts = bucket.spread.map((e) => `${spreadLabel(e)} ${e.orders}`);
  return `Spread across campaigns: ${parts.join(', ')}`;
}

function ScopeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" />
      <path d="M11 7h5a2 2 0 0 1 2 2v4" strokeDasharray="2 2" />
    </svg>
  );
}

function CapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 15a8 8 0 0 1 16 0" />
      <path d="M2 15h20" />
      <path d="M12 7V4" />
    </svg>
  );
}

function AudIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5a3 3 0 0 1 0 6M21 20a6 6 0 0 0-5-5.9" />
    </svg>
  );
}
